import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useQuery } from 'react-apollo';

import Button from '../../../../../Button';
import Alert from '../../../../../Alert';
import FormGroup from '../../../../../FormGroup';
import useDebounce from '../../../../../../../hooks/useDebounce';
import usePricingCategories from '../../../../../hooks/usePricingCategories';
import useAccountPrintAssets from '../../../../../../../apps/pro/views/Account/AccountPrintAssets/components/AccountPrintAsset/hooks/useAccountPrintAssets';
import {
  SystemPrintAssetGroups,
  SystemPrintAssetImages,
} from '../../../../../../../graphql/queries/system-print-assets';
import {
  getGroupDefaultMember,
  getGroupDisplayName,
  getGroupOrientation,
  groupMatchesPricingCategories,
} from '../../../../../../../util/print-asset-groups';
import {
  getPrintAssetDisplayImageUrl,
  getPrintAssetOrientation,
} from '../../../../../../../util/print-assets';
import { PRINT_ASSET_TYPES } from '../../../../../../../constants/print-assets';
import { PRICING_CATEGORY_TYPE } from '../../../../../../../constants/pricing-categories';
import CardDesignerAssetGroupPanel from './components/CardDesignerAssetGroupPanel';

export const ASSET_PICKER_TAB = {
  WALDO_LIBRARY: 'WALDO_LIBRARY',
  YOUR_UPLOADS: 'YOUR_UPLOADS',
};

const propTypes = {
  className: PropTypes.string,
  printAssetType: PropTypes.oneOf(Object.values(PRINT_ASSET_TYPES)).isRequired,
  searchPlaceholder: PropTypes.string,
  onAssetSelect: PropTypes.func.isRequired,
  uploadControl: PropTypes.node,
  currentAssetImageId: PropTypes.string,
  onSaveAsAsset: PropTypes.func,
  defaultPricingCategoryIds: PropTypes.arrayOf(PropTypes.string),
  defaultTab: PropTypes.oneOf(Object.values(ASSET_PICKER_TAB)),
  // Opting in renders the Waldo library as print asset groups (one tile per
  // group, sticky expansion with smart toggle + variants).
  onGroupSmartToggle: PropTypes.func,
  currentGroupId: PropTypes.string,
  orientation: PropTypes.string,
};

const CardDesignerAssetPicker = ({
  className,
  printAssetType,
  searchPlaceholder = 'Search your assets..',
  onAssetSelect,
  uploadControl,
  currentAssetImageId,
  onSaveAsAsset,
  defaultPricingCategoryIds = [],
  defaultTab,
  onGroupSmartToggle,
  currentGroupId,
  orientation,
}) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 500);
  const [selectedTab, setSelectedTab] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [categoryFilterEnabled, setCategoryFilterEnabled] = useState(
    defaultPricingCategoryIds.length > 0,
  );

  const {
    printAssets,
    printAssetsTotal,
    setSearchQuery,
    setSelectedPricingCategoryIds,
  } = useAccountPrintAssets({
    printAssetType,
  });

  const { pricingCategories } = usePricingCategories();

  // The generic Waldo families (Flame, Fog, Rock, …) are tagged with the
  // "Other" category only, yet they suit every sport: the pre-filter always
  // lets them through alongside the print's own categories.
  const genericCategoryIds = pricingCategories
    .filter(({ type }) => type === PRICING_CATEGORY_TYPE.OTHER)
    .map(({ id }) => id);
  const filterPricingCategoryIds =
    categoryFilterEnabled && defaultPricingCategoryIds.length > 0
      ? [...new Set([...defaultPricingCategoryIds, ...genericCategoryIds])]
      : [];
  const filterKey = filterPricingCategoryIds.join(',');

  useEffect(() => {
    setSearchQuery(debouncedQuery);
  }, [debouncedQuery]);

  useEffect(() => {
    setSelectedPricingCategoryIds(filterPricingCategoryIds);
  }, [filterKey]);

  const classes = classNames({
    [className]: !!className,
  });

  // The Waldo library is the SYSTEM pool, server-filtered with the same
  // search + category inputs as the account assets.
  const { data: { systemPrintAssets = {} } = {} } = useQuery(
    SystemPrintAssetImages,
    {
      variables: {
        print_asset_type: printAssetType,
        search_query: debouncedQuery || undefined,
        pricing_category_ids:
          filterPricingCategoryIds.length > 0
            ? filterPricingCategoryIds
            : undefined,
      },
      fetchPolicy: 'cache-first',
    },
  );

  const { data: { systemPrintAssetGroups = {} } = {} } = useQuery(
    SystemPrintAssetGroups,
    {
      variables: { print_asset_group_type: printAssetType },
      fetchPolicy: 'cache-first',
      skip: !onGroupSmartToggle,
    },
  );

  // Assets encoding another orientation in their name are excluded, same as
  // groups below.
  const systemAssets = (systemPrintAssets.records ?? []).filter(printAsset => {
    const assetOrientation = getPrintAssetOrientation(printAsset);

    return (
      !orientation || !assetOrientation || assetOrientation === orientation
    );
  });
  const userAssets = printAssets;

  // Group mode: the Waldo library lists print asset groups instead of every
  // asset — far fewer images to load. Groups encoding another orientation in
  // their name are excluded.
  const groupRecords = (systemPrintAssetGroups.records ?? []).filter(group => {
    const groupOrientation = getGroupOrientation(group);

    return (
      !orientation || !groupOrientation || groupOrientation === orientation
    );
  });
  const groupMode = !!onGroupSmartToggle && groupRecords.length > 0;
  // The category pre-filter judges a group by its default member's asset.
  const filteredGroups =
    filterPricingCategoryIds.length > 0
      ? groupRecords.filter(group =>
          groupMatchesPricingCategories(group, filterPricingCategoryIds),
        )
      : groupRecords;
  // Resolved against the filtered set so a group the pre-filter drops takes
  // its expanded panel with it, rather than leaving stale variants clickable.
  const selectedGroup =
    filteredGroups.find(group => group.id === selectedGroupId) ?? null;

  // An element that already carries an asset opens on the uploads tab, the
  // only place its current image is shown and removable. Otherwise default to
  // the Waldo library when it has anything to offer. Explicit user selection
  // — and the caller's defaultTab, e.g. a smart matched background — win.
  const fallbackTab =
    !currentAssetImageId && (systemAssets.length > 0 || groupMode)
      ? ASSET_PICKER_TAB.WALDO_LIBRARY
      : ASSET_PICKER_TAB.YOUR_UPLOADS;
  const activeTab = selectedTab ?? defaultTab ?? fallbackTab;

  const activeTabAssets =
    activeTab === ASSET_PICKER_TAB.WALDO_LIBRARY ? systemAssets : userAssets;

  // Picking an asset sets currentAssetImageId, which flips the fallback above
  // to the uploads tab — browsing the library would keep throwing the user out
  // of it. Pin the tab the pick was made from.
  const onTabAssetSelect = printAsset => {
    setSelectedTab(activeTab);
    onAssetSelect(printAsset);
  };

  const filteredCategoryNames = pricingCategories
    .filter(({ id }) => defaultPricingCategoryIds.includes(id))
    .map(({ name }) => name);

  const isAlreadyInLibrary =
    !!currentAssetImageId &&
    printAssets.some(
      printAsset => printAsset.asset_image?.id === currentAssetImageId,
    );

  return (
    <div className={classes}>
      {categoryFilterEnabled && (
        <Alert type="info" className="mt-2 text-xs">
          Only showing assets for{' '}
          {filteredCategoryNames.join(', ') || 'the selected categories'}
          {genericCategoryIds.length > 0
            ? ' and the generic families'
            : ''}.{' '}
          <Button
            btnStyle="link"
            className="p-0 text-xs"
            onClick={() => setCategoryFilterEnabled(false)}
          >
            Show all assets
          </Button>
        </Alert>
      )}

      <div className="card-designer-asset-picker-tabs">
        <Button
          btnStyle="reset"
          className={classNames({
            active: activeTab === ASSET_PICKER_TAB.WALDO_LIBRARY,
          })}
          onClick={() => setSelectedTab(ASSET_PICKER_TAB.WALDO_LIBRARY)}
        >
          Waldo library
        </Button>
        <Button
          btnStyle="reset"
          className={classNames({
            active: activeTab === ASSET_PICKER_TAB.YOUR_UPLOADS,
          })}
          onClick={() => setSelectedTab(ASSET_PICKER_TAB.YOUR_UPLOADS)}
        >
          Your uploads
        </Button>
      </div>

      {activeTab === ASSET_PICKER_TAB.YOUR_UPLOADS && uploadControl}

      {activeTab === ASSET_PICKER_TAB.YOUR_UPLOADS &&
        onSaveAsAsset &&
        currentAssetImageId &&
        !isAlreadyInLibrary && (
          <Button
            btnStyle="link"
            className="mt-1 p-0 text-sm"
            onClick={onSaveAsAsset}
          >
            Save as {printAssetType.toLowerCase()} asset
          </Button>
        )}

      {/* Group tiles carry no user-meaningful names — no search there. The
          system total is search filtered, so keep the input mounted while a
          query is set: a no-match search has to stay clearable. */}
      {!(activeTab === ASSET_PICKER_TAB.WALDO_LIBRARY && groupMode) &&
        (!!query ||
          printAssetsTotal > 0 ||
          (systemPrintAssets.total ?? 0) > 0) && (
          <FormGroup
            id="query"
            name="query"
            label="Query"
            labelSrOnly
            className="mt-2"
            placeholder={searchPlaceholder}
            onChange={({ target }) => setQuery(target.value)}
            value={query}
          />
        )}

      {activeTab === ASSET_PICKER_TAB.WALDO_LIBRARY && groupMode ? (
        <Fragment>
          {selectedGroup && (
            <CardDesignerAssetGroupPanel
              group={selectedGroup}
              currentAssetImageId={currentAssetImageId}
              smartEnabled={currentGroupId === selectedGroup.id}
              onSmartToggle={onGroupSmartToggle}
              onAssetSelect={onTabAssetSelect}
            />
          )}
          {filteredGroups.length > 0 ? (
            <div className="card-designer-print-assets-container">
              {filteredGroups.map(group => (
                <Button
                  key={group.id}
                  title={getGroupDisplayName(group)}
                  className={classNames({
                    'card-designer-print-assets-item': true,
                    selected: group.id === selectedGroupId,
                  })}
                  btnStyle="reset"
                  onClick={() => {
                    if (group.id === selectedGroupId) {
                      setSelectedGroupId(null);

                      return;
                    }

                    setSelectedGroupId(group.id);

                    // Expanding a family previews it straight away, so the
                    // card shows what was clicked. Smart Color Matching stays
                    // as it was — the panel offers that as the other path.
                    const defaultAsset =
                      getGroupDefaultMember(group)?.print_asset;

                    if (defaultAsset?.asset_image?.id) {
                      onTabAssetSelect(defaultAsset);
                    }
                  }}
                >
                  <img
                    src={getPrintAssetDisplayImageUrl(
                      getGroupDefaultMember(group)?.print_asset,
                    )}
                    alt={getGroupDisplayName(group)}
                    className="card-designer-print-assets-item-image"
                  />
                </Button>
              ))}
            </div>
          ) : (
            <p className="mt-2 mb-0 text-sm text-light">
              No Waldo library assets available yet.
            </p>
          )}
        </Fragment>
      ) : activeTabAssets.length > 0 ? (
        <div className="card-designer-print-assets-container">
          {activeTabAssets.map(printAsset => (
            <Button
              key={printAsset.id}
              className="card-designer-print-assets-item"
              btnStyle="reset"
              onClick={() => onTabAssetSelect(printAsset)}
            >
              <img
                src={getPrintAssetDisplayImageUrl(printAsset)}
                alt={printAsset.name}
                className="card-designer-print-assets-item-image"
              />
            </Button>
          ))}
        </div>
      ) : (
        activeTab === ASSET_PICKER_TAB.WALDO_LIBRARY && (
          <p className="mt-2 mb-0 text-sm text-light">
            No Waldo library assets available yet.
          </p>
        )
      )}
    </div>
  );
};

CardDesignerAssetPicker.propTypes = propTypes;

export default CardDesignerAssetPicker;
